import LinearProgress from '@/global/components/LinearProgress'
import { cn } from '@/lib/utils'
import { Dialog as DialogPrimitive } from 'radix-ui'
import { HTMLProps } from 'react'

/** `wrap` sai do `HTMLProps` — lá é o atributo de `<textarea>`, uma string. */
interface TitleProps extends Omit<HTMLProps<HTMLSpanElement>, 'wrap'> {
    children?: React.ReactNode
    BoxProps?: HTMLProps<HTMLDivElement>
    /**
     * Título em várias linhas, em vez de uma linha só que rola na horizontal.
     * Para pergunta de confirmação, que costuma trazer o nome do registro.
     */
    wrap?: boolean
    /**
     * Progresso de 0 a 100, ou `'indeterminate'` para carregamento contínuo.
     * Exibido na base, sobreposto: não desloca o layout.
     */
    progress?: number | 'indeterminate'
    /**
     * O que o diálogo faz com o registro ("Editar rotina", "Excluir funcionário"),
     * em linha menor acima do `children`, que passa a ser o nome do registro.
     */
    action?: string
    /** Elemento alinhado à direita do título, fora do bloco de ação e nome (ex.: abas). */
    endAdornment?: React.ReactNode
}

export type { TitleProps as DialogTitleProps }

/**
 * `DialogPrimitive.Title` sem estilo próprio (`asChild`): é ele que dá nome ao
 * diálogo para o leitor de tela e evita o aviso do Radix por título ausente.
 */
export function Title({
    children,
    BoxProps,
    wrap,
    progress,
    action: acao,
    endAdornment: fim,
    ...props
}: TitleProps) {
    return (
        <DialogPrimitive.Title asChild>
            <span
                {...props}
                className={cn(
                    'relative overflow-y-hidden overflow-x-auto',
                    wrap && 'overflow-x-hidden',
                    'p-4 text-lg font-medium min-h-fit',
                    props.className,
                )}
            >
                <div
                    {...BoxProps}
                    className={cn(
                        'flex flex-row flex-nowrap',
                        'px-2 sm:px-4 justify-between items-center',
                        'min-w-max gap-8',
                        // `min-w-max` é o que segura tudo em uma linha; sem ele
                        // a linha quebra e o texto longo passa a caber.
                        wrap && 'min-w-0 flex-wrap wrap-break-word',
                        // Com adorno, o título encolhe até `min-w-32` para o
                        // adorno caber; abaixo disso a linha rola.
                        fim && 'min-w-0',
                        BoxProps?.className,
                    )}
                >
                    {acao ? (
                        <div className={cn('flex flex-col', fim && 'min-w-32')}>
                            <span
                                className={cn(
                                    'text-xs font-normal text-muted-foreground select-none',
                                    fim && 'truncate',
                                )}
                            >
                                {acao}
                            </span>
                            <span className={cn(fim && 'truncate')}>
                                {children}
                            </span>
                        </div>
                    ) : fim ? (
                        <div className="min-w-32 truncate">{children}</div>
                    ) : (
                        children
                    )}
                    {fim && <div className="shrink-0 min-w-fit">{fim}</div>}
                </div>
                <LinearProgress
                    variant={
                        progress === 'indeterminate'
                            ? 'indeterminate'
                            : 'determinate'
                    }
                    value={typeof progress === 'number' ? progress : 0}
                    className={cn(
                        'absolute inset-x-0 bottom-0 transition-opacity duration-300',
                        progress === undefined && 'opacity-0',
                    )}
                />
            </span>
        </DialogPrimitive.Title>
    )
}

Title.isTituloDoDialogo = true
